import axios from '@axios'
import { limparUltimaRota } from '@/velta/utils/ultimaRota'

// Importação dinâmica para evitar dependência circular
// As funções são carregadas apenas quando necessárias
const getPlanPermissionsModule = () => import('@/libs/acl/planPermissions')

export default {
    namespaced: true,
    state: {
        userData: {},
        empresaData: {},
        notifications: [],
        tenantConfig: [],
    },
    mutations: {
        setUserData(state, userData) {
            state.userData = userData;
        },
        setTenantConfig(state, tenantConfig) {
            state.tenantConfig = tenantConfig;
        },
        setEmpresaData(state, empresaData) {
            state.empresaData = empresaData;
        },
        setNotifications(state, notifications) {
            state.notifications = notifications;
        },
        setMarkAllAsReadNotification(state) {
            state.notifications.forEach(notification => {
                notification.lido = 1;
            });
        },
        setMarkAsReadNotificationById(state, id) {
            const notificationIndex = state.notifications.findIndex(notification => notification.id === id);
            if (notificationIndex !== -1) {
                state.notifications[notificationIndex].lido = 1;
            }
        }
    },
    getters: {
        authenticated(state) {
            return Object.keys(JSON.parse(JSON.stringify(state.userData))).length > 0
        },
        tenantConfig(state) {
            return state.tenantConfig
        },
        empresaData(state) {
            return state.empresaData
        },
        abilities(state) {
            // `permissoes` pode faltar: o backend devolve 200 e só loga quando o
            // cálculo de permissões falha (AuthController::fetchAuthUser). Sem o
            // fallback, o spread de `undefined` estoura um TypeError dentro do
            // guard de rota, o `next()` nunca é chamado e o app fica travado no
            // splash — sem barra de endereço, no app das lojas.
            return [
                {
                    subject: 'any'
                },
                ...(state.userData['permissoes'] || [])
            ]
        }
    },
    actions: {
        async logout(ctx) {
            // Limpa permissões do plano e para o refresh automático
            const planModule = await getPlanPermissionsModule()
            planModule.stopAutoRefresh()
            planModule.clearPlanPermissionsFromStorage()
            localStorage.removeItem('veltacar_login_empresa')
            // Sessão encerrada de propósito: a última tela não pode sobreviver
            // para o próximo login (tablet de recepção trocando de operador).
            limparUltimaRota()
            // Cache SWR das listagens não pode vazar entre sessões/usuários
            const { default: listCache } = await import('@/velta/components/crud/mixins/listCache')
            listCache.clearAll()
            // Contadores do menu também são por empresa
            const { limparCache } = await import('@/libs/navegacao/cacheContadores')
            limparCache()
            ctx.commit('navegacao/limpar', null, { root: true })
            await axios.get('/api/auth/logout').then(() => ctx.commit('setUserData', {})).catch(() => ctx.commit('setUserData', {}))
        },
        async logoutCliente(ctx) {
            localStorage.removeItem('veltacar_login_cliente')
            await axios.get('/api/cliente_geral/logout').then(() => ctx.commit('setUserData', {})).catch(() => ctx.commit('setUserData', {}))
        },
        login(ctx, data) {
            return axios.get('/sanctum/csrf-cookie').then((res) => {
                return axios({
                    method: 'POST',
                    url: '/api/auth/login',
                    validateStatus: (status) => status === 201,
                    data
                }).then(() => {
                    return ctx.dispatch('fetchAuthUser');
                })
            });
        },
        loginCliente(ctx, data) {
            return axios.get('/sanctum/csrf-cookie').then((res) => {
                return axios({
                    method: 'POST',
                    url: '/api/cliente_geral/login',
                    validateStatus: (status) => status === 201,
                    data
                }).then(() => {
                    return ctx.dispatch('fetchAuthUserCliente');
                })
            });
        },
        redefinirsenha(ctx, data) {
            return axios.get('/sanctum/csrf-cookie').then((res) => {
                return axios({
                    method: 'POST',
                    url: '/api/auth/redefinirsenha',
                    validateStatus: (status) => status === 201,
                    data
                });
            });
        },
        redefinirSenhaCliente(ctx, data) {
            return axios.get('/sanctum/csrf-cookie').then((res) => {
                return axios({
                    method: 'POST',
                    url: '/api/cliente_geral/redefinirSenha',
                    validateStatus: (status) => status === 201,
                    data
                });
            });
        },
        async fetchAuthUser(ctx) {
            return await axios({
                method: 'GET',
                url: '/api/auth/fetchAuthUser',
                validateStatus: (status) => status === 200
            }).then(async (response) => {
                ctx.commit('setUserData', response.data.userData);
                ctx.commit('setEmpresaData', response.data.empresaData)
                ctx.commit('setNotifications', response.data.notifications);
                ctx.commit('setTenantConfig', response.data.tenantConfig);

                // Inicializa permissões do plano no localStorage e inicia auto-refresh
                const planModule = await getPlanPermissionsModule()
                await planModule.initPlanPermissions()
            }).catch(async (error) => {
                ctx.commit('setUserData', {});
                ctx.commit('setEmpresaData', {});
                ctx.commit('setNotifications', {});
                ctx.commit('setTenantConfig', {});
                // Limpa permissões do plano em caso de erro
                try {
                    const planModule = await getPlanPermissionsModule()
                    planModule.stopAutoRefresh()
                    planModule.clearPlanPermissionsFromStorage()
                } catch (e) {
                    // Ignora erro se módulo não carregar
                }
            });
        },
        /**
         * Força o recálculo das permissões no backend (sem usar cache).
         * Usado para redundância quando o frontend detecta bloqueio por plano.
         * @returns {Promise<boolean>} - true se as permissões foram atualizadas, false caso contrário
         */
        async refreshPermissions(ctx) {
            try {
                const response = await axios({
                    method: 'GET',
                    url: '/api/minhaspermissoes/refresh',
                    validateStatus: (status) => status === 201
                });

                if (response.data && response.data.arrPermissoes) {
                    const currentUserData = ctx.state.userData;
                    const oldPermissions = JSON.stringify(currentUserData.permissoes || []);
                    const newPermissions = JSON.stringify(response.data.arrPermissoes);

                    // Se as permissões mudaram, atualiza o estado
                    if (oldPermissions !== newPermissions) {
                        ctx.commit('setUserData', {
                            ...currentUserData,
                            permissoes: response.data.arrPermissoes
                        });
                        return true; // Permissões foram atualizadas
                    }
                }
                return false; // Permissões não mudaram
            } catch (error) {
                console.error('Erro ao atualizar permissões:', error);
                return false;
            }
        },
        async afterUpdateOrCreate(ctx, data) {
            return await axios({
                method: 'POST',
                url: '/api/usuario/afterUpdateOrCreate',
                validateStatus: (status) => status === 200,
                data,
            })
        },
        async fetchAuthUserCliente(ctx) {
            return await axios({
                method: 'GET',
                url: '/api/cliente_geral/fetchAuthUser',
                validateStatus: (status) => status === 200
            }).then((response) => {
                ctx.commit('setUserData', response.data.userData);
                ctx.commit('setNotifications', response.data.notifications);
            }).catch((error) => {
                ctx.commit('setUserData', {});
                ctx.commit('setNotifications', {});
            });
        },
        markAllAsRead(ctx) {
            ctx.commit('setMarkAllAsReadNotification');
        },
        markNotificationAsRead(ctx, id) {
            ctx.commit('setMarkAsReadNotificationById', id);
        },
        /**
         * Força atualização das permissões do plano do backend
         * @returns {Promise<Object|null>}
         */
        async refreshPlanPermissions() {
            const planModule = await getPlanPermissionsModule()
            return await planModule.fetchPlanPermissionsFromBackend()
        },
        /**
         * Obtém as permissões do plano do localStorage
         * @returns {Object|null}
         */
        async getPlanPermissions() {
            const planModule = await getPlanPermissionsModule()
            return planModule.getPlanPermissionsFromStorage()
        },
        /**
         * Reinicia o auto-refresh das permissões do plano
         * Útil quando o usuário volta para a aplicação após tempo inativo
         */
        async restartPlanPermissionsAutoRefresh() {
            const planModule = await getPlanPermissionsModule()
            planModule.startAutoRefresh()
        }
    }
}
